/**
 * Hover a citation to see the passage of the cited paper that best supports the sentence it is in
 * (matched in the paper's PDF text or abstract), with its page; "Open beside" shows it in the side panel.
 */
import { syntaxTree } from "@codemirror/language";
import type { EditorState, Extension } from "@codemirror/state";
import { hoverTooltip } from "@codemirror/view";
import { post } from "@/lib/api";

interface Evidence {
  available: boolean;
  reason: string | null;
  matches: { sentence: string; page: number | null; pageLabel: string | null; overlap: number; from: "pdf" | "abstract" }[];
  item: { title: string; year: number | null; authors: { family?: string; literal?: string }[] };
}

const cache = new Map<string, Promise<Evidence | null>>();

function evidence(itemId: string, claim: string, page: number | null) {
  const key = `${itemId}|${page ?? ""}|${claim}`;
  if (!cache.has(key)) cache.set(key, post<Evidence>(`/api/library/${itemId}/evidence`, { claim, page }).catch(() => null));
  return cache.get(key)!;
}

/** The citation link (or paper link) under the pointer. */
function linkAt(state: EditorState, pos: number) {
  for (const side of [1, -1] as const) {
    for (let n: ReturnType<ReturnType<typeof syntaxTree>["resolveInner"]> | null = syntaxTree(state).resolveInner(pos, side); n; n = n.parent) {
      if (n.name !== "Link") continue;
      const url = n.getChild("URL");
      const href = url ? state.sliceDoc(url.from, url.to) : "";
      return /^bl:\/\/(cite|paper)\/[0-9a-f-]{36}/i.test(href) ? { from: n.from, to: n.to, href } : null;
    }
  }
  return null;
}

/** The sentence the citation belongs to (links are masked so their dots do not end sentences). */
export function sentenceAround(state: EditorState, from: number, to: number) {
  const line = state.doc.lineAt(from);
  const chars = line.text.split("");
  syntaxTree(state).iterate({
    from: line.from,
    to: line.to,
    enter: (n) => {
      if (n.name !== "Link") return;
      for (let i = Math.max(n.from, line.from); i < Math.min(n.to, line.to); i++) chars[i - line.from] = "x";
    },
  });
  const masked = chars.join("");
  const a = from - line.from;
  const b = to - line.from;
  let start = 0;
  for (const m of masked.slice(0, a).matchAll(/[.!?](?=\s)/g)) start = m.index! + 1;
  const after = masked.slice(b).search(/[.!?](?=\s|$)/);
  const end = after < 0 ? line.text.length : b + after + 1;
  return line.text.slice(start, end).trim();
}

export function citeHover(openLink: { current: ((href: string, context: string) => void) | null | undefined }): Extension {
  return hoverTooltip(
    async (view, pos) => {
      const link = linkAt(view.state, pos);
      if (!link) return null;
      const m = /^bl:\/\/(?:cite|paper)\/([0-9a-f-]{36})(?:\?(.*))?$/i.exec(link.href);
      if (!m) return null;
      const page = Number(new URLSearchParams(m[2] ?? "").get("page")) || null;
      const claim = sentenceAround(view.state, link.from, link.to);
      const ev = await evidence(m[1], claim, page);
      return {
        pos: link.from,
        end: link.to,
        above: true,
        create: () => {
          const dom = document.createElement("div");
          dom.className = "cm-evidence-pop";
          const head = document.createElement("p");
          head.className = "cm-evidence-head";
          const who = ev?.item.authors?.[0];
          head.textContent = ev ? `${ev.item.title || "Untitled paper"} — ${who?.family ?? who?.literal ?? "Unknown"}${(ev.item.authors?.length ?? 0) > 1 ? " et al." : ""}, ${ev.item.year ?? "n.d."}` : "The cited paper";
          dom.append(head);
          if (!ev) {
            const p = document.createElement("p");
            p.textContent = "The paper could not be read.";
            dom.append(p);
          } else if (!ev.available || !ev.matches.length) {
            const p = document.createElement("p");
            p.className = "cm-evidence-none";
            p.textContent = ev.reason ?? "No passage of the paper closely matches this sentence. Check the citation.";
            dom.append(p);
          } else {
            for (const x of ev.matches.slice(0, 2)) {
              const q = document.createElement("blockquote");
              q.textContent = `“${x.sentence}”`;
              const meta = document.createElement("p");
              meta.className = "cm-evidence-meta";
              meta.textContent = `${x.from === "abstract" ? "Abstract" : `PDF page ${x.page}${x.pageLabel && x.pageLabel !== String(x.page) ? ` (printed ${x.pageLabel})` : ""}`} · ${Math.round(x.overlap * 100)}% of the sentence's key words`;
              dom.append(q, meta);
            }
          }
          const open = document.createElement("button");
          open.type = "button";
          open.textContent = "Open beside the text";
          open.addEventListener("mousedown", (e) => {
            e.preventDefault();
            openLink.current?.(link.href, claim);
          });
          dom.append(open);
          return { dom };
        },
      };
    },
    { hoverTime: 350 },
  );
}
